export interface CardShellOptions {
  /** Raises the card instead of thickening its border — nothing reflows. */
  isSelected?: boolean;
}

/**
 * The shell for a card rendered *outside* the calendar — an appointment in the
 * patient drawer's Terminkontext, Dauertermine, the Rx view, the unscheduled
 * list and the drag shelf, or the Rx summary above an Rx's appointment list.
 *
 * This is deliberately the same recipe as `.mc-event` in
 * [MobiliCalendar.css](./MobiliCalendar/MobiliCalendar.css): white fill, one
 * hairline at `--ink-edge`, `--radius-card`, `--elev-rest` at rest stepping to
 * `--elev-hover` on pointer. A record should not change its appearance
 * depending on whether you are looking at it in the grid or in a list.
 *
 * `.mc-event` also carries a 4px accent border. Callers that have a single
 * category colour (an Rx summary) should set it as an inline `borderLeft`, the
 * way EventBlock sets `--ev-accent`. An *appointment* card does not: it can span
 * several prescriptions, so its accent moves one level in, onto each slot row in
 * `TherapySlotRow`, where consecutive rows on the same Rx merge into one rail.
 *
 * Consumers must drop any `border`, `borderRadius` or `backgroundColor` from
 * their own `sx`: MUI's emotion styles are injected after Tailwind's, so an
 * `sx` prop silently wins over every class here.
 */
export const cardShell = ({ isSelected }: CardShellOptions = {}): string =>
  [
    "bg-card rounded-card transition-shadow duration-fast",
    // Shadow is state (design-system rule 2), so selection lifts the card
    // rather than thickening its border — a 2px border on one state reflows
    // every row around it.
    isSelected ? "shadow-selected" : "shadow-rest hover:shadow-hover",
    // `border-solid` is load-bearing — preflight is disabled, so Tailwind's
    // base border-style reset never lands and `border` alone draws nothing.
    // One border for every card: a crimson "today" outline used to live here,
    // but on a card that already carries date, time and status it read as an
    // alert rather than as orientation.
    "border border-solid border-ink-edge",
  ].join(" ");
